import { Crown } from 'lucide-react';
import { UserProfile } from '@/types/fitness';

interface HeroBannerProps {
  perfil: UserProfile;
  grasaFaltante: string;
  isEstimated?: boolean;
}

const GOAL_LABELS: Record<string, string> = {
  lose_fat: 'Definición Élite',
  gain_muscle: 'Construcción Muscular',
  maintenance: 'Mantenimiento',
  performance: 'Alto Rendimiento',
  health: 'Salud Integral',
};

const ENFOQUE_LABELS: Record<string, string> = {
  lose_fat: 'Perder Grasa',
  gain_muscle: 'Ganar Músculo',
  maintenance: 'Mantenimiento',
  performance: 'Mejorar Rendimiento',
  health: 'Mejorar Salud',
};

function formatGoalName(nombreMeta: string): string {
  const part = nombreMeta.split(' - ')[1] || nombreMeta;
  const key = part.toLowerCase().replace(/\s+/g, '_');
  return GOAL_LABELS[key] || part.replace(/_/g, ' ');
}

function formatEnfoque(enfoque: string): string {
  const key = enfoque.toLowerCase().replace(/\s+/g, '_');
  return ENFOQUE_LABELS[key] || enfoque.replace(/_/g, ' ');
}

export function HeroBanner({ perfil, grasaFaltante, isEstimated = false }: HeroBannerProps) {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-primary mb-8 shadow-2xl group noise-overlay">
      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-accent/20 z-10 gradient-shift"></div>
      
      {/* Carbon texture */}
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] z-10"></div>
      
      {/* Subtle radial glow */}
      <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-accent/3 rounded-full blur-[120px] z-10"></div>
      
      <div className="relative z-20 p-5 md:p-16 flex flex-col md:flex-row justify-between items-start md:items-end gap-6 md:gap-8">
        <div className="space-y-3 md:space-y-4 min-w-0">
          <div className="flex items-center gap-2 text-accent animate-slide-in">
            <Crown size={18} className="animate-float" />
            <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em]">Insane Force Team</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-8xl font-black italic uppercase tracking-tighter text-primary-foreground leading-[0.85] break-words animate-fade-in glow-text-accent">
            {formatGoalName(perfil.nombreMeta)}
          </h1>
          {/* Subtitle con efecto "shine" — la luz roja recorre el texto
              cada 4 segundos. Sutil, no invasivo, mantiene el formato
              limpio de una sola línea. */}
          <p
            className="text-shine-light text-xs md:text-sm font-black uppercase tracking-wider md:tracking-widest max-w-md leading-relaxed animate-fade-in"
            style={{ animationDelay: '0.2s' }}
          >
            {formatEnfoque(perfil.enfoque)} | {perfil.edad} Años | Meta: {perfil.metaGrasa}% Grasa
          </p>
        </div>
        
        <div className="bg-accent text-accent-foreground p-4 md:p-8 min-w-[140px] md:min-w-[200px] text-center transform md:skew-x-[-10deg] rounded-xl md:rounded-none shadow-[10px_10px_0px_rgba(255,255,255,0.1)] self-end md:self-auto shrink-0 glow-accent shimmer animate-scale-in">
          <div className="md:skew-x-[10deg]">
            <div className="text-[10px] md:text-xs font-black uppercase tracking-widest opacity-60 mb-1">
              Gap Meta
            </div>
            <div className="text-4xl md:text-6xl font-black tracking-tighter leading-none animate-count-up">
              -{grasaFaltante}<span className="text-xl md:text-2xl">%</span>
            </div>
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-wide mt-1 md:mt-2">
              Grasa Corporal
            </div>
            {isEstimated && (
              <div className="mt-2 px-2 py-0.5 rounded-full bg-white/20 text-[9px] font-black uppercase tracking-wider">
                ⚡ Estimado
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
